import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Spin,
  message,
  Divider,
  Typography,
} from "antd";
import {
  TeamOutlined,
  UserOutlined,
  StarOutlined,
  StarFilled,
  ApartmentOutlined,
} from "@ant-design/icons";
import { organizationAPI } from "../../../../api/organizationApi";
import { fetchUsers } from "../../../../api/userManagement";

const UNASSIGNED = "待分配组织";
const isUnassignedName = (name) => name === UNASSIGNED;
const { Text } = Typography;

const RightInspector = ({
  target,
  selectedOrg,
  selectedUserIds,
  organizationFlatList,
  onInspect,
  onReloadTree,
  canUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [userAssignedOrgs, setUserAssignedOrgs] = useState([]);
  const [userDefaultOrg, setUserDefaultOrg] = useState(null);
  const [allUsersMap, setAllUsersMap] = useState(new Map());

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchUsers({ page: 1, page_size: 500 });
        const map = new Map((res.items || []).map((u) => [u.id, u]));
        setAllUsersMap(map);
      } catch {
        /* ignore */
      }
    };
    load();
  }, []);

  const loadOrgDetail = useCallback(async (orgId) => {
    try {
      setLoading(true);
      const res = await organizationAPI.getOrganizationUsers(orgId);
      setOrgMembers(res.items || []);
    } catch {
      message.error("加载组织详情失败");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserDetail = useCallback(async (userId) => {
    try {
      setLoading(true);
      const [assignedRes, defaultRes] = await Promise.allSettled([
        organizationAPI.getUserOrganizations(userId),
        organizationAPI.getUserDefaultOrganization(userId),
      ]);
      setUserAssignedOrgs(
        assignedRes.status === "fulfilled"
          ? assignedRes.value.organizations || []
          : []
      );
      setUserDefaultOrg(
        defaultRes.status === "fulfilled"
          ? defaultRes.value.default_organization || null
          : null
      );
    } catch {
      message.error("加载用户详情失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!target) {
      setOrgMembers([]);
      setUserAssignedOrgs([]);
      setUserDefaultOrg(null);
      return;
    }
    if (target.type === "org") {
      loadOrgDetail(target.id);
    } else if (target.type === "user") {
      loadUserDetail(target.id);
    }
  }, [target, loadOrgDetail, loadUserDetail]);

  const handleSetDefault = async (userId, orgId) => {
    try {
      await organizationAPI.setUserDefaultOrganization(userId, orgId);
      message.success("默认组织已设置");
      await loadUserDetail(userId);
      onReloadTree();
    } catch (err) {
      message.error(err.response?.data?.detail || "设置失败");
    }
  };

  const handleAssignNewOrg = async (userId, orgId) => {
    try {
      await organizationAPI.assignUserToOrganization(orgId, userId);
      message.success("已加入组织");
      await loadUserDetail(userId);
      onReloadTree();
    } catch (err) {
      message.error(err.response?.data?.detail || "分配失败");
    }
  };

  if (!target) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#999" }}>
        <ApartmentOutlined style={{ fontSize: 48, marginBottom: 16 }} />
        <div>选择一个组织或用户查看详情</div>
      </div>
    );
  }

  if (target.type === "org") {
    const org = organizationFlatList.find((o) => o.id === target.id);
    if (!org) return null;
    return (
      <Spin spinning={loading}>
        <div style={{ padding: 16 }}>
          <Card size="small" title="组织详情">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="名称">
                <Text strong>{org.name}</Text>
                {isUnassignedName(org.name) && (
                  <Tag color="orange" style={{ marginLeft: 6 }}>
                    系统兜底
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="描述">
                {org.description || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="ID">{org.id}</Descriptions.Item>
              <Descriptions.Item label="路径">
                {org.pathLabel || org.name}
              </Descriptions.Item>
              <Descriptions.Item label="父组织">
                {org.parent_id
                  ? organizationFlatList.find((o) => o.id === org.parent_id)
                      ?.name || `ID ${org.parent_id}`
                  : "根组织"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Divider style={{ margin: "12px 0" }} />
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            <TeamOutlined style={{ marginRight: 6 }} />
            直接成员 ({orgMembers.length})
          </div>
          <Table
            columns={[
              {
                title: "用户名",
                dataIndex: "username",
                key: "username",
                width: 100,
              },
              {
                title: "姓名",
                dataIndex: "full_name",
                key: "full_name",
                width: 90,
                render: (v) => v || "-",
              },
              {
                title: "状态",
                dataIndex: "is_active",
                key: "is_active",
                width: 70,
                render: (v) => (
                  <Tag color={v ? "green" : "default"} size="small">
                    {v ? "活跃" : "禁用"}
                  </Tag>
                ),
              },
              {
                title: "操作",
                key: "action",
                width: 80,
                render: (_, record) => (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onInspect("user", record.id)}
                  >
                    查看
                  </Button>
                ),
              },
            ]}
            dataSource={orgMembers}
            rowKey="id"
            size="small"
            pagination={orgMembers.length > 10 ? { pageSize: 10 } : false}
          />
        </div>
      </Spin>
    );
  }

  if (target.type === "user") {
    const userId = target.id;
    const userInfo = allUsersMap.get(userId) || {
      username: `用户 #${userId}`,
    };
    const assignableOrgs = organizationFlatList.filter(
      (o) =>
        !userAssignedOrgs.some((a) => a.id === o.id) &&
        !isUnassignedName(o.name)
    );

    return (
      <Spin spinning={loading}>
        <div style={{ padding: 16 }}>
          <Card
            size="small"
            title={
              <span>
                <UserOutlined style={{ marginRight: 6 }} />
                用户详情
              </span>
            }
          >
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
              {userInfo.username}
            </div>
            <div style={{ color: "#666", fontSize: 12 }}>
              {userInfo.full_name || "-"} | {userInfo.email || "-"}
            </div>
            {userInfo.roles && userInfo.roles.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Space size={4} wrap>
                  {userInfo.roles.map((role) => (
                    <Tag color="blue" key={role.id} size="small">
                      {role.name}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
          </Card>
          <Divider style={{ margin: "12px 0" }} />
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            直接所属组织
          </div>
          <Space direction="vertical" style={{ width: "100%" }}>
            {userAssignedOrgs.map((org) => (
              <div
                key={org.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Space>
                  <span>{org.name}</span>
                  {isUnassignedName(org.name) && (
                    <Tag color="orange">兜底</Tag>
                  )}
                  {userDefaultOrg?.id === org.id && (
                    <Tag color="blue" icon={<StarFilled />}>
                      默认
                    </Tag>
                  )}
                </Space>
                <Space>
                  {canUpdate &&
                    !isUnassignedName(org.name) &&
                    userDefaultOrg?.id !== org.id && (
                      <Button
                        type="link"
                        size="small"
                        icon={<StarOutlined />}
                        onClick={() => handleSetDefault(userId, org.id)}
                      >
                        设默认
                      </Button>
                    )}
                </Space>
              </div>
            ))}
            {userAssignedOrgs.length === 0 && (
              <Text type="secondary">无直接所属组织</Text>
            )}
          </Space>

          {canUpdate && assignableOrgs.length > 0 && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                分配新组织
              </div>
              <Select
                size="small"
                style={{ width: "100%" }}
                placeholder="选择组织加入"
                options={assignableOrgs.map((o) => ({
                  value: o.id,
                  label: o.pathLabel || o.name,
                }))}
                onChange={(orgId) => handleAssignNewOrg(userId, orgId)}
              />
            </>
          )}
        </div>
      </Spin>
    );
  }

  return null;
};

export default RightInspector;
